import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Ledger {
  id: number;
  ledgerName: string;
  ledgerCode: string;
  ledgerType: string;
  natureID: number;
  nature?: { id: number; natureName: string };
  status: boolean | string;
  isEditable?: boolean;
}

export default function Ledgers() {
  const navigate = useNavigate();
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getLedgers();
  }, [page, limit, search]);

  const getLedgers = async () => {
    setLoading(true);
    const response = await request.get("/api/getLedgers", {
      search,
      limit,
      offset: page,
      orderColumn: "ledgerName",
      orderDirection: "asc",
    });
    setLedgers(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "ledgerName",
      label: "Ledger Name",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "ledgerCode",
      label: "Code",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "ledgerType",
      label: "Type",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "nature",
      label: "Nature",
      minWidth: 150,
      format: (value) => value?.natureName || "-",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true || value === "Active"} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={
            row.isEditable === 0
              ? []
              : [
                  {
                    icon: <EditIcon />,
                    tooltip: "Edit",
                    onClick: () => navigate(`/admin/ledgers/${row.id}`),
                  },
                ]
          }
        />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Ledgers"
        showSearch
        searchPlaceholder="Search ledgers..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Ledger",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/ledgers/create");
          },
        }}
      />

      <DataTable
        columns={columns}
        rows={ledgers}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
    </PageWrapper>
  );
}
